import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, KeyRound, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "admin_fog_head", label: "Admin Flight Operation Group Head" },
  { value: "fog_head", label: "Flight Operation Group Head" },
  { value: "admin_chief_pilot", label: "Admin Chief Pilot" },
  { value: "admin_chief_fa", label: "Admin Chief Flight Attendant" },
  { value: "pilot_management", label: "Pilot Management" },
  { value: "fa_management", label: "FA Management" },
  { value: "flight_operation_support", label: "Flight Operation Support" },
  { value: "medical", label: "Medical" },
  { value: "document_controller", label: "Document Controller" },
  { value: "jktof", label: "JKTOF" },
  { value: "jktdo", label: "JKTDO (Read Only)" },
  { value: "viewer", label: "Viewer" },
  { value: "administrator", label: "Administrator (Legacy Superuser)" },
];

const roleName = (r) => ROLE_OPTIONS.find(x=>x.value===r)?.label || r;

export default function UserManagementPage() {
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [openNew, setOpenNew] = useState(false);
  const [openEdit, setOpenEdit] = useState(null);
  const [openReset, setOpenReset] = useState(null);
  const [form, setForm] = useState({ email:"", name:"", role:"viewer", department:"", password:"", is_active: true });
  const [newPw, setNewPw] = useState("");

  const { data = [], isLoading } = useQuery({ queryKey:["users"], queryFn: async () => (await apiClient.get("/users")).data, enabled: isAdmin() });

  const mCreate = useMutation({
    mutationFn: async (b) => (await apiClient.post("/users", b)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["users"]}); toast.success("User dibuat"); setOpenNew(false); setForm({ email:"", name:"", role:"viewer", department:"", password:"", is_active:true }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal"),
  });
  const mUpdate = useMutation({
    mutationFn: async ({id, body}) => (await apiClient.put(`/users/${id}`, body)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["users"]}); toast.success("User diperbarui"); setOpenEdit(null); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal"),
  });
  const mReset = useMutation({
    mutationFn: async ({id, pw}) => (await apiClient.post(`/users/${id}/reset-password`, { new_password: pw })).data,
    onSuccess: () => { toast.success("Password direset"); setOpenReset(null); setNewPw(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal"),
  });

  if (!isAdmin()) return <Navigate to="/dashboard" replace />;

  const total = data.length;
  const active = data.filter(u=>u.is_active !== false).length;
  const inactive = total - active;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#00A651]">System</div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>User Management</h1>
        <p className="text-sm text-slate-500 mt-1">Kelola pengguna, peran, dan status akses portal.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["Total User",total,"bg-slate-100 text-slate-700"],["Aktif",active,"bg-emerald-100 text-emerald-700"],["Nonaktif",inactive,"bg-red-100 text-red-700"]].map(([l,v,t]) => (
          <Card key={l} className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t}`}><UsersIcon className="w-5 h-5"/></div>
            <div><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{l}</div>
            <div className="text-xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{v}</div></div>
          </CardContent></Card>
        ))}
      </div>

      <Card className="border-slate-200"><CardContent className="p-4 space-y-4">
        <div className="flex justify-end">
          <Button onClick={()=>setOpenNew(true)} className="bg-[#00A651] hover:bg-[#008741]" data-testid="user-add-btn"><Plus className="w-4 h-4 mr-1"/> Tambah User</Button>
        </div>
        <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
          <Table className="fodm-table"><TableHeader className="bg-slate-50"><TableRow>
            <TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Departemen</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Login Terakhir</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader><TableBody>
            {isLoading ? <TableRow><TableCell colSpan={7} className="py-6 text-center text-slate-500">Memuat...</TableCell></TableRow>
            : data.length === 0 ? <TableRow><TableCell colSpan={7} className="py-6 text-center italic text-slate-500">Belum ada user.</TableCell></TableRow>
            : data.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="mono text-xs">{u.email}</TableCell>
                <TableCell className="text-xs">{u.department || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{roleName(u.role)}</Badge></TableCell>
                <TableCell>{u.is_active === false ? <Badge className="bg-red-100 text-red-700">Nonaktif</Badge> : <Badge className="bg-emerald-100 text-emerald-700">Aktif</Badge>}</TableCell>
                <TableCell className="text-[11px] text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleString("id-ID") : <span className="italic">Belum pernah</span>}</TableCell>
                <TableCell className="text-right"><div className="flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" onClick={()=>setOpenEdit({...u})} data-testid={`user-edit-${u.email}`}><Pencil className="w-4 h-4"/></Button>
                  <Button size="sm" variant="ghost" onClick={()=>setOpenReset(u)} title="Reset Password"><KeyRound className="w-4 h-4"/></Button>
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody></Table>
        </div>
      </CardContent></Card>

      {/* CREATE */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Tambah User Baru</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="user-form-name"/></div>
            <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="user-form-email"/></div>
            <div><Label>Departemen</Label><Input value={form.department} onChange={(e)=>setForm({...form, department:e.target.value})}/></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v)=>setForm({...form, role:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map(r=><SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Password Awal</Label><Input type="password" value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} autoComplete="new-password" data-testid="user-form-password"/></div>
            <div className="col-span-2 flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v)=>setForm({...form, is_active:v})}/> <Label className="text-xs">Aktifkan user langsung</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenNew(false)}>Batal</Button>
            <Button className="bg-[#00A651] hover:bg-[#008741]" onClick={()=>mCreate.mutate(form)} disabled={mCreate.isPending} data-testid="user-form-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT */}
      <Dialog open={!!openEdit} onOpenChange={(o)=>!o && setOpenEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Ubah User</DialogTitle></DialogHeader>
          {openEdit && <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Email</Label><Input value={openEdit.email} disabled className="bg-slate-50"/></div>
            <div className="col-span-2"><Label>Nama</Label><Input value={openEdit.name} onChange={(e)=>setOpenEdit({...openEdit, name:e.target.value})}/></div>
            <div><Label>Departemen</Label><Input value={openEdit.department || ""} onChange={(e)=>setOpenEdit({...openEdit, department:e.target.value})}/></div>
            <div><Label>Role</Label>
              <Select value={openEdit.role} onValueChange={(v)=>setOpenEdit({...openEdit, role:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map(r=><SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center gap-2 p-2 rounded-lg border border-slate-200">
              <Switch checked={openEdit.is_active !== false} onCheckedChange={(v)=>setOpenEdit({...openEdit, is_active:v})}/>
              <Label className="text-xs">{openEdit.is_active !== false ? "Aktif" : "Nonaktif — user tidak bisa login"}</Label>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenEdit(null)}>Batal</Button>
            <Button className="bg-[#00A651] hover:bg-[#008741]" onClick={()=>mUpdate.mutate({id: openEdit.id, body: { name: openEdit.name, role: openEdit.role, department: openEdit.department, is_active: openEdit.is_active }})} disabled={mUpdate.isPending}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD */}
      <Dialog open={!!openReset} onOpenChange={(o)=>!o && setOpenReset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reset Password · {openReset?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Password Baru</Label><Input type="password" value={newPw} onChange={(e)=>setNewPw(e.target.value)} autoComplete="new-password"/></div>
            <p className="text-xs text-slate-500">Password akan di-hash bcrypt. Sampaikan password baru ke user melalui kanal yang aman dan minta segera diganti setelah login.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenReset(null)}>Batal</Button>
            <Button className="bg-[#00A651] hover:bg-[#008741]" onClick={()=>mReset.mutate({id: openReset.id, pw: newPw})} disabled={mReset.isPending}>Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
